import React, { useRef, useState, useEffect } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

// Contexts
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../../context/configurationContext';

// Utils
import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../../util/search';
import { createResourceLocatorString } from '../../../../util/routes';
import { stringifyDateToISO8601 } from '../../../../util/dates';

// Components
import { Form, LocationAutocompleteInput, FieldSelect } from '../../../../components';
import FilterDateRange from '../../../PageBuilder/Primitives/SearchCTA/FilterDateRange/FilterDateRange';
import FilterGuests from '../../../PageBuilder/Primitives/SearchCTA/FilterGuests/FilterGuests';
import FilterLocation from '../../../PageBuilder/Primitives/SearchCTA/FilterLocation/FilterLocation';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const isValidDateValue = d => {
  if (!d) return false;
  const t = new Date(d).getTime();
  return !Number.isNaN(t);
};

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button
        className={css.searchSubmit}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
        type="submit"
      >
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>

      <Field
        name="keywords"
        render={({ input }) => (
          <input
            className={isMobile ? css.mobileInput : css.desktopInput}
            {...input}
            id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
            data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
            ref={inputRef}
            type="text"
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            autoComplete="off"
          />
        )}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  const submitButton = () => <div />;

  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
            submitButton={submitButton}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
          />
        );
      }}
    />
  );
};

const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const containerRef = useRef(null);

  const intl = useIntl();
  const config = useConfiguration();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const { appConfig, ...restOfProps } = props;

  const [activeField, setActiveField] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);

  useEffect(() => {
    if (!activeField) return;

    const onDocMouseDown = e => {
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setActiveField(null);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [activeField]);

  const navigateToSearch = values => {
    let queryParams = {};

    if (values?.keywords) {
      queryParams.keywords = values.keywords;
    }

    if (values?.guests) {
      const v = String(values.guests);
      queryParams.pub_guest_capacity = `${v},${6 + 1}`;
    }

    if (values?.dateRange?.startDate && values?.dateRange?.endDate) {
      const start = stringifyDateToISO8601(values.dateRange.startDate);
      const end = stringifyDateToISO8601(values.dateRange.endDate);
      queryParams.dates = `${start},${end}`;
    }

    const loc = values?.location;
    if (loc?.selectedPlace) {
      const {
        search,
        selectedPlace: { origin, bounds },
      } = loc;

      queryParams.address = search;
      queryParams.bounds = bounds;

      if (isOriginInUse(config) && origin) {
        queryParams.origin = `${origin.lat},${origin.lng}`;
      }
    }

    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams);
    history.push(to);

    searchInpuRef?.current?.blur();
  };

  const onLocationChange = location => {
    if (location?.selectedPlace) {
      setActiveField(null);
    }
  };

  const onSubmit = values => {
    if (submitDisabled) return;
    if (isKeywordsSearch) {
      navigateToSearch({ keywords: values.keywords });
    } else {
      navigateToSearch(values);
    }
    setActiveField(null);
  };

  const selectableGuests = 6;
  const quantities = [...Array(selectableGuests).keys()].map(i => i + 1);

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={onSubmit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
          values,
        } = formRenderProps;
        const selectedGuests = values?.guests;
        const selectedLocation = values?.location?.search;
        const startDate = values?.dateRange?.startDate;
        const endDate = values?.dateRange?.endDate;

        const hasValidDates = isValidDateValue(startDate) && isValidDateValue(endDate);

        const start = hasValidDates ? stringifyDateToISO8601(startDate) : null;
        const end = hasValidDates ? stringifyDateToISO8601(endDate) : null;

        const dateRangeString = hasValidDates ? `${start} - ${end}` : null;


        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            {isKeywordsSearch ? (
              <KeywordSearchField
                keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
              />
            ) : (
              <div className={css.searchContainer} ref={containerRef}>
                <div
                  className={css.segment}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveField('location')}
                  onKeyDown={e => e.key === 'Enter' && setActiveField('location')}
                >
                  <FilterLocation setSubmitDisabled={setSubmitDisabled} alignLeft />
                </div>

                <div className={css.divider} />

                <div
                  className={css.segment}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveField('guests')}
                  onKeyDown={e => e.key === 'Enter' && setActiveField('guests')}
                >
                  <div className={css.segmentInputWrap} onClick={e => e.stopPropagation()}>
                    <FilterGuests name="guests" minGuests={1} maxGuests={6} alignLeft />
                  </div>
                </div>

                <div className={css.divider} />

                <div
                  className={css.segment}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveField('dates')}
                  onKeyDown={e => e.key === 'Enter' && setActiveField('dates')}
                >
                  <div className={css.segmentInputWrap} onClick={e => e.stopPropagation()}>
                    <FilterDateRange config={config} alignLeft />
                  </div>
                </div>

                <button
                  className={css.searchSubmit}
                  disabled={submitDisabled}
                  type="submit"
                  aria-label="Search"
                >
                  <span className={css.searchText}>Search</span>
                  <IconSearchDesktop />
                </button>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
